import { CommonModule } from '@angular/common';
import {
  AfterViewInit, Component, OnDestroy, OnInit, computed, inject, signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, merge, startWith } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Auth, authState } from '@angular/fire/auth';
import { Timestamp } from '@angular/fire/firestore';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subscription, filter, switchMap, take } from 'rxjs';
import { BookingService } from '../../services/booking.service';
import { BookingContractService } from '../../services/booking-contract.service';
import { AssetService } from '../../services/asset.service';
import {
  Booking,
} from '../../models/booking.model';
import {
  onlineFeeEurosFromBooking,
  ownerBalanceEurosFromBooking,
} from '../../shared/booking/booking-amounts.utils';
import {
  formalizationPlazoLabel,
  isFastTrackFormalizationEligible,
  resolveFormalizationDeadlineMode,
  resolveFormalizationWindowDays,
} from '../../shared/booking/formalization-deadline.utils';
import { OwnerSettings } from '../../models/user.model';

@Component({
  selector: 'app-booking-formalization',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './booking-formalization.component.html',
})
export class BookingFormalizationComponent implements OnInit, OnDestroy, AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(Auth);
  private fb = inject(FormBuilder);
  private bookingService = inject(BookingService);
  private contractService = inject(BookingContractService);
  private assetService = inject(AssetService);
  private sanitizer = inject(DomSanitizer);

  readonly bookingId = signal('');
  readonly sessionId = signal(this.route.snapshot.queryParamMap.get('session_id') ?? '');
  readonly booking = signal<Booking | null>(null);
  readonly contractHtmlRaw = signal('');
  readonly ownerSettings = signal<Partial<OwnerSettings> | null>(null);
  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly loadError = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly submitted = signal(false);
  readonly contractAccepted = signal(false);
  readonly receiptFile = signal<File | null>(null);
  readonly receiptFileName = signal('');
  readonly fastTrackBizum = signal(false);
  readonly isDropOver = signal(false);

  readonly form = this.fb.nonNullable.group({
    userLegalName: ['', [Validators.required, Validators.minLength(5)]],
    userDni: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(12), spanishIdValidator]],
    userPhone: ['', [Validators.required, spanishMobileValidator]],
  });

  private readonly formValid = toSignal(
    merge(this.form.statusChanges, this.form.valueChanges).pipe(
      startWith(null),
      map(() => this.form.valid),
    ),
    { initialValue: this.form.valid },
  );

  readonly status = computed(() => this.booking()?.status ?? '');
  readonly isPendingFormalization = computed(() => this.status() === 'PENDING_FORMALIZATION');
  readonly isPendingVerification = computed(() => this.status() === 'PENDING_VERIFICATION');
  readonly isConfirmed = computed(() => this.status() === 'CONFIRMED');
  readonly isResolvingFromSession = computed(() =>
    this.waitingForSessionBooking() && !!this.sessionId() && !this.booking(),
  );

  readonly depositDue = computed(() => {
    const b = this.booking();
    if (!b) return 0;
    return ownerBalanceEurosFromBooking(b);
  });

  readonly isFullPayment = computed(() => {
    const b = this.booking();
    return b?.payment_status === 'FULL_PAYMENT' || b?.payoutModeAtCheckout === 'STRIPE';
  });

  readonly ownerPaymentReady = computed(() => {
    if (this.isFullPayment()) return true;
    const s = this.ownerSettings();
    if (!s) return false;
    const sepaOk  = !!s.sepaEnabled  && !!s.accountHolder?.trim() && !!s.iban?.trim();
    const bizumOk = !!s.bizumEnabled && !!s.mobilePhone?.trim();
    const cashOk  = !!s.cashEnabled;
    return sepaOk || bizumOk || cashOk;
  });

  readonly contractHtmlSafe = computed<SafeHtml | null>(() => {
    const html = this.contractHtmlRaw().trim();
    return html ? this.sanitizer.bypassSecurityTrustHtml(html) : null;
  });

  readonly formalizationExpired = computed(() => {
    const b = this.booking();
    if (!b?.formalizationDeadline || !this.isPendingFormalization()) return false;
    const deadline = b.formalizationDeadline instanceof Timestamp ?
      b.formalizationDeadline.toDate() :
      new Date(b.formalizationDeadline as unknown as string);
    return deadline.getTime() < Date.now();
  });

  readonly deadlineLabel = computed(() => {
    const b = this.booking();
    if (!b?.formalizationDeadline) return '';
    const deadline = b.formalizationDeadline instanceof Timestamp ?
      b.formalizationDeadline.toDate() :
      new Date(b.formalizationDeadline as unknown as string);
    return deadline.toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  /** Ticks every 30 seconds so countdown signals re-evaluate. */
  readonly nowMs = signal(Date.now());
  private nowInterval?: ReturnType<typeof setInterval>;

  /** Milliseconds until the formalization deadline. Negative when expired. */
  readonly msUntilDeadline = computed(() => {
    const b = this.booking();
    if (!b?.formalizationDeadline) return 0;
    const deadline = b.formalizationDeadline instanceof Timestamp ?
      b.formalizationDeadline.toDate().getTime() :
      new Date(b.formalizationDeadline as unknown as string).getTime();
    return deadline - this.nowMs();
  });

  /** Human-readable countdown — "Quedan 2 d 4 h" / "Quedan 6 h 12 min" / "Menos de 1 minuto". */
  readonly deadlineCountdown = computed(() => {
    const ms = this.msUntilDeadline();
    if (ms <= 0) return 'Plazo expirado';
    const totalMinutes = Math.floor(ms / 60_000);
    const days  = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins  = totalMinutes % 60;
    if (days >= 1)  return `${days} d ${hours} h`;
    if (hours >= 1) return `${hours} h ${mins} min`;
    if (mins >= 1)  return `${mins} min`;
    return 'Menos de 1 minuto';
  });

  /**
   * Urgency bucket of the countdown — drives banner color.
   *   safe     → > 24 h
   *   warning  → 1–24 h
   *   critical → < 1 h
   *   expired  → ≤ 0 (handled separately by `formalizationExpired`)
   */
  readonly deadlineUrgency = computed<'safe' | 'warning' | 'critical' | 'expired'>(() => {
    const ms = this.msUntilDeadline();
    if (ms <= 0)             return 'expired';
    if (ms < 60 * 60_000)    return 'critical';        // < 1 hour
    if (ms < 24 * 60 * 60_000) return 'warning';       // < 24 hours
    return 'safe';
  });

  /** Localized date label — "sábado, 15 de junio de 2026". */
  readonly bookingDateLabel = computed(() => {
    const b = this.booking();
    if (!b?.date) return '';
    const d = new Date(`${b.date}T00:00:00`);
    if (Number.isNaN(d.getTime())) return b.date;
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  });

  /** "16:00 – 22:00" */
  readonly bookingHoursLabel = computed(() => {
    const b = this.booking();
    if (!b?.startTime || !b.endTime) return '';
    return `${b.startTime} – ${b.endTime}`;
  });

  /** Total amount paid in commission (Step 1). */
  readonly commissionPaid = computed(() => {
    const b = this.booking();
    if (!b) return 0;
    return onlineFeeEurosFromBooking(b);
  });

  /** Total of the reservation. */
  readonly totalAmount = computed(() => this.booking()?.totalPrice ?? 0);

  readonly fastTrackEligible = computed(() => {
    const b = this.booking();
    if (!b || this.isFullPayment()) return false;
    if (!isFastTrackFormalizationEligible(b)) return false;
    return !!this.ownerSettings()?.bizumEnabled && !!this.ownerSettings()?.mobilePhone?.trim();
  });

  readonly receiptSatisfied = computed(() =>
    this.isFullPayment() ||
    !!this.receiptFile() ||
    (this.fastTrackEligible() && this.fastTrackBizum()),
  );

  readonly canSubmit = computed(() =>
    this.formValid() &&
    this.contractAccepted() &&
    this.receiptSatisfied() &&
    this.ownerPaymentReady() &&
    !this.submitting() &&
    this.isPendingFormalization() &&
    !this.formalizationExpired(),
  );

  private sub?: Subscription;
  private sessionResolveTimeout?: number;
  private syncAttempted = false;
  readonly waitingForSessionBooking = signal(false);

  ngOnInit(): void {
    // Tick `nowMs` so the deadline countdown re-evaluates without user input.
    // 30 s is granular enough — the countdown shows minutes, not seconds.
    this.nowInterval = setInterval(() => this.nowMs.set(Date.now()), 30_000);

    const paramId = this.route.snapshot.paramMap.get('bookingId')
      ?? this.route.snapshot.paramMap.get('id')
      ?? '';
    if (paramId) {
      this.bookingId.set(paramId);
      this.watchBooking(paramId);
      return;
    }

    const sessionId = this.sessionId();
    if (!sessionId) {
      this.loadError.set(true);
      this.loading.set(false);
      return;
    }

    this.waitingForSessionBooking.set(true);
    this.sessionResolveTimeout = window.setTimeout(() => {
      if (!this.booking() && this.sessionId()) {
        this.waitingForSessionBooking.set(false);
        this.loadError.set(true);
        this.loading.set(false);
      }
    }, 90_000);

    this.sub = authState(this.auth).pipe(
      filter((user): user is NonNullable<typeof user> => !!user),
      take(1),
      switchMap(user => this.bookingService.watchBookingByCheckoutSession(sessionId, user.uid)),
    ).subscribe({
      next: b => this.onSessionBookingSnapshot(b),
      error: () => {
        this.waitingForSessionBooking.set(false);
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.sessionResolveTimeout !== undefined) {
      window.clearTimeout(this.sessionResolveTimeout);
    }
    if (this.nowInterval !== undefined) {
      clearInterval(this.nowInterval);
    }
  }

  ngAfterViewInit(): void {
    this.#scrollToFormalizationStep();
  }

  private onSessionBookingSnapshot(b: Booking | null): void {
    if (b) {
      if (this.sessionResolveTimeout !== undefined) {
        window.clearTimeout(this.sessionResolveTimeout);
        this.sessionResolveTimeout = undefined;
      }
      this.waitingForSessionBooking.set(false);
      this.onBookingLoaded(b);
      return;
    }

    this.loading.set(false);
    this.loadError.set(false);
    void this.#trySyncBookingFromSession();
  }

  async #trySyncBookingFromSession(): Promise<void> {
    const sessionId = this.sessionId().trim();
    if (!sessionId || this.syncAttempted || this.booking()) return;
    this.syncAttempted = true;
    try {
      await this.bookingService.syncBookingFromCheckoutSession(sessionId);
    } catch (err) {
      console.warn('[BookingFormalization] syncBookingFromCheckoutSession failed:', err);
    }
  }

  private onBookingLoaded(b: Booking | null): void {
    this.booking.set(b);
    if (b?.id) {
      this.bookingId.set(b.id);
      void this.loadContractPreview(b);
      void this.loadOwnerPayment(b);
    }
    this.loading.set(false);
    if (!b) this.loadError.set(true);
    if (b?.status === 'PENDING_VERIFICATION') this.submitted.set(true);
    if (b?.status === 'CONFIRMED') this.submitted.set(true);
    if (b?.status === 'PENDING_FORMALIZATION') {
      this.submitted.set(false);
      this.prefillFormalizationForm(b);
      queueMicrotask(() => this.#scrollToFormalizationStep());
    }
  }

  private prefillFormalizationForm(b: Booking): void {
    const legalName = b.userLegalName?.trim()
      || b.guestBillingLegalName?.trim()
      || '';
    const dni = b.userDni?.trim()
      || b.guestBillingTaxId?.trim()
      || '';
    if (legalName) {
      this.form.patchValue({
        userLegalName: legalName,
        userDni: dni,
        userPhone: b.userPhone?.trim() ?? '',
      });
    }
    if (b.contractSignedAt) {
      this.contractAccepted.set(true);
    }
  }

  readonly receiptRejected = computed(() => !!this.booking()?.depositReceiptRejectedAt);

  /** Deep-link from payment confirmation email (`?step=deposit|receipt|contract`). */
  #scrollToFormalizationStep(): void {
    const step = this.route.snapshot.queryParamMap.get('step');
    const idByStep: Record<string, string> = {
      deposit: 'tfr-step-deposit',
      receipt: 'tfr-step-receipt',
      contract: 'tfr-step-contract',
    };
    const targetId = step ? idByStep[step] : null;
    if (!targetId) return;
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private watchBooking(id: string): void {
    this.sub = this.bookingService.watchBooking(id).subscribe({
      next: b => this.onBookingLoaded(b),
      error: () => {
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  private async loadContractPreview(b: Booking): Promise<void> {
    if (!b.spaceId || !b.date || !b.startTime || !b.endTime) return;
    const space = await this.assetService.getSpaceById(b.spaceId);
    if (!space) return;
    const html = await this.contractService.composeUnifiedContractHtml(space, {
      totalPrice: b.totalPrice ?? 0,
      date: b.date,
      startTime: b.startTime,
      endTime: b.endTime,
      ownerName: b.ownerAccountHolder ?? '',
      bookingSnapshots: {
        bookingPriceCents: b.bookingPriceCents,
        applicationFeeCents: b.applicationFeeCents,
        commissionAmountCents: b.commissionAmountCents,
        commissionRateApplied: b.commissionRateApplied,
        payoutModeAtCheckout: b.payoutModeAtCheckout,
        payment_status: b.payment_status,
      },
    });
    this.contractHtmlRaw.set(html);
  }

  private ownerPaymentFromBooking(b: Booking): Partial<OwnerSettings> | null {
    // Honor the snapshot of toggles persisted on the booking at payment time.
    // Legacy bookings (pre-toggles) fall back to "method active when its data
    // is filled" — same rule the server uses for backward compatibility.
    const sepaEnabled  = b.ownerSepaEnabled  ?? !!b.ownerIban?.trim();
    const bizumEnabled = b.ownerBizumEnabled ?? !!b.ownerMobilePhone?.trim();
    const cashEnabled  = b.ownerCashEnabled  ?? false;
    if (!sepaEnabled && !bizumEnabled && !cashEnabled) return null;
    return {
      accountHolder: sepaEnabled  ? (b.ownerAccountHolder?.trim() ?? '') : '',
      iban:          sepaEnabled  ? (b.ownerIban?.trim() ?? '')          : '',
      mobilePhone:   bizumEnabled ? (b.ownerMobilePhone?.trim() ?? '')   : '',
      sepaEnabled,
      bizumEnabled,
      cashEnabled,
    };
  }

  /** Loads payment details from owner Ajustes (callable) with booking snapshot fallback. */
  private async loadOwnerPayment(b: Booking): Promise<void> {
    const snapshot = this.ownerPaymentFromBooking(b);
    if (snapshot) this.ownerSettings.set(snapshot);
    if (!b.id) {
      if (!snapshot) this.ownerSettings.set(null);
      return;
    }
    try {
      this.ownerSettings.set(await this.bookingService.fetchOwnerPaymentDetails(b.id));
    } catch {
      if (!snapshot) this.ownerSettings.set(null);
    }
  }

  formatIban(iban: string | undefined): string {
    const cleaned = (iban ?? '').replace(/\s+/g, '').toUpperCase();
    if (!cleaned) return '';
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  }

  readonly formalizationWindowDays = computed(() => {
    const b = this.booking();
    if (!b?.date) return 7;
    const paymentMs = b.paymentTimestamp instanceof Timestamp ?
      b.paymentTimestamp.toMillis() :
      undefined;
    return resolveFormalizationWindowDays(b.date, b.formalizationWindowDays, paymentMs);
  });

  readonly formalizationDeadlineMode = computed(() => {
    const b = this.booking();
    return resolveFormalizationDeadlineMode(
      b?.formalizationDeadlineMode,
      b?.formalizationWindowDays,
    );
  });

  readonly formalizationPlazoSummary = computed(() =>
    formalizationPlazoLabel(this.formalizationWindowDays(), this.formalizationDeadlineMode()),
  );

  onReceiptSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.setReceiptFile(file);
  }

  onReceiptDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDropOver.set(false);
    const file = event.dataTransfer?.files?.[0] ?? null;
    this.setReceiptFile(file);
  }

  onReceiptDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDropOver.set(true);
  }

  onReceiptDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDropOver.set(false);
  }

  triggerReceiptInput(input: HTMLInputElement): void {
    input.click();
  }

  private setReceiptFile(file: File | null): void {
    this.receiptFile.set(file);
    this.receiptFileName.set(file?.name ?? '');
    if (file) this.fastTrackBizum.set(false);
  }

  onFastTrackToggle(checked: boolean): void {
    this.fastTrackBizum.set(checked);
    if (checked) {
      this.receiptFile.set(null);
      this.receiptFileName.set('');
    }
  }

  async submitFormalization(): Promise<void> {
    if (!this.canSubmit()) return;
    const id = this.bookingId();
    if (!id) return;

    this.submitting.set(true);
    this.submitError.set(null);

    try {
      let receiptUrl: string | undefined;
      const useFastTrack = !this.isFullPayment() && this.fastTrackBizum();
      if (!this.isFullPayment() && !useFastTrack) {
        const file = this.receiptFile();
        if (!file) return;
        receiptUrl = await this.bookingService.uploadDepositReceipt(id, file);
      }
      const v = this.form.getRawValue();
      await this.bookingService.completeFormalization({
        bookingId: id,
        userLegalName: v.userLegalName.trim(),
        userDni: v.userDni.trim().toUpperCase(),
        userPhone: v.userPhone.replace(/\s+/g, ''),
        ...(receiptUrl ? { depositReceiptUrl: receiptUrl } : {}),
        ...(useFastTrack ? { fastTrackBizum: true } : {}),
        contractAccepted: true,
        userAgent: navigator.userAgent,
      });
      this.submitted.set(true);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message
        ?? 'No se pudo enviar la formalización. Inténtalo de nuevo.';
      this.submitError.set(msg);
    } finally {
      this.submitting.set(false);
    }
  }

  goToBookings(): void {
    this.router.navigate(['/mis-reservas']);
  }

  showError(controlName: 'userLegalName' | 'userDni' | 'userPhone'): boolean {
    const c = this.form.controls[controlName];
    return c.invalid && (c.dirty || c.touched);
  }
}

function spanishIdValidator(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value ?? '').toString().trim().toUpperCase();
  if (!raw) return null;
  const dni = /^(\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/i.test(raw);
  const passport = /^[A-Z0-9]{5,12}$/i.test(raw);
  return dni || passport ? null : { spanishId: true };
}

function spanishMobileValidator(control: AbstractControl): ValidationErrors | null {
  const digits = (control.value ?? '').toString().replace(/\s+/g, '').replace(/^\+34/, '');
  if (!digits) return null;
  return /^[67]\d{8}$/.test(digits) ? null : { spanishMobile: true };
}
